import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Clock, CheckCircle, AlertCircle, ArrowUpRight, Download, Calendar, CreditCard, Building2, FileText, Tag, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';

const ResellerBilling = () => {
  const [billingData, setBillingData] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [payoutRequest, setPayoutRequest] = useState({
    amount: '',
    payout_method: 'bank_transfer',
    notes: ''
  });
  const [billingSettings, setBillingSettings] = useState({
    billing_email: '',
    payout_method: 'bank_transfer',
    payout_details: {
      bank_name: '',
      account_number: '',
      routing_number: '',
      account_holder: ''
    },
    tax_id: '',
    business_address: ''
  });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [pricing, setPricing] = useState({ starter: 29, pro: 79, enterprise: 199 });
  const [platformDefaults, setPlatformDefaults] = useState({ starter: 29, pro: 79, enterprise: 199 });
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(() => {
    fetchBillingData();
    fetchCommissions();
    fetchPayouts();
    fetchPricing();
  }, []);

  const fetchBillingData = async () => {
    try {
      const res = await api.get('/reseller/billing');
      setBillingData(res.data);
      if (res.data.billing_settings) {
        setBillingSettings(prev => ({ ...prev, ...res.data.billing_settings }));
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissions = async (page = 1) => {
    try {
      const res = await api.get(`/reseller/commissions?page=${page}&per_page=10`);
      setCommissions(res.data.commissions);
      setPagination({
        page: res.data.page,
        totalPages: res.data.total_pages
      });
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    }
  };

  const fetchPayouts = async () => {
    try {
      const res = await api.get('/reseller/payouts');
      setPayouts(res.data.payouts);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    }
  };

  const handleRequestPayout = async () => {
    try {
      const requestData = {
        payout_method: payoutRequest.payout_method,
        notes: payoutRequest.notes
      };
      if (payoutRequest.amount) {
        requestData.amount = parseFloat(payoutRequest.amount);
      }
      
      const res = await api.post('/reseller/payouts/request', requestData);
      toast.success('Payout request submitted successfully');
      setShowPayoutModal(false);
      setPayoutRequest({ amount: '', payout_method: 'bank_transfer', notes: '' });
      fetchBillingData();
      fetchPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    }
  };

  const handleUpdateBillingSettings = async () => {
    try {
      await api.put('/reseller/billing/settings', billingSettings);
      toast.success('Billing settings updated');
      setShowSettingsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await api.get('/reseller/pricing');
      setPricing(res.data.pricing);
      setPlatformDefaults(res.data.platform_defaults);
    } catch (e) { /* silent */ }
  };

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      await api.put('/reseller/pricing', pricing);
      toast.success('Plan pricing updated');
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100 text-slate-800'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const stats = billingData?.stats || {};

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 overflow-auto">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Billing & Earnings</h1>
              <p className="text-slate-600">Track your commissions, earnings, and payouts</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowSettingsModal(true)}>
                <CreditCard size={16} className="mr-2" />
                Billing Settings
              </Button>
              <Button 
                onClick={() => setShowPayoutModal(true)}
                disabled={stats.pending_earnings <= 0}
              >
                <DollarSign size={16} className="mr-2" />
                Request Payout
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Total Earnings</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.total_earnings)}</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <DollarSign className="text-green-600" size={24} />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">All time earnings</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Pending Earnings</p>
                    <p className="text-2xl font-bold text-yellow-600">{formatCurrency(stats.pending_earnings)}</p>
                  </div>
                  <div className="p-3 bg-yellow-100 rounded-full">
                    <Clock className="text-yellow-600" size={24} />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Available for payout</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Monthly Revenue</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.mrr)}</p>
                  </div>
                  <div className="p-3 bg-indigo-100 rounded-full">
                    <TrendingUp className="text-indigo-600" size={24} />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">From your customers</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Commission Rate</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.commission_rate || 20}%</p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <ArrowUpRight className="text-purple-600" size={24} />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Your earnings rate</p>
              </CardContent>
            </Card>
          </div>

          {/* Projected Earnings Banner */}
          {stats.projected_monthly_earnings > 0 && (
            <Card className="mb-8 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-indigo-100 text-sm">Projected Monthly Earnings</p>
                    <p className="text-3xl font-bold">{formatCurrency(stats.projected_monthly_earnings)}</p>
                    <p className="text-indigo-200 text-sm mt-1">
                      Based on {stats.commission_rate}% of {formatCurrency(stats.mrr)} MRR
                    </p>
                  </div>
                  <div className="text-right">
                    <Calendar size={48} className="text-white/30" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs for Commissions, Payouts and Pricing */}
          <Tabs defaultValue="commissions" className="space-y-6">
            <TabsList>
              <TabsTrigger value="commissions">Commission History</TabsTrigger>
              <TabsTrigger value="payouts">Payout History</TabsTrigger>
              <TabsTrigger value="pricing">Plan Pricing</TabsTrigger>
            </TabsList>

            <TabsContent value="commissions">
              <Card>
                <CardHeader>
                  <CardTitle>Commission History</CardTitle>
                  <CardDescription>Your earnings from customer subscriptions</CardDescription>
                </CardHeader>
                <CardContent>
                  {commissions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Period</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Customer</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Revenue</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Your Earnings</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commissions.map((commission) => (
                            <tr key={commission.commission_id} className="border-b last:border-0 hover:bg-slate-50">
                              <td className="py-3 px-4 text-sm text-slate-900">{commission.period}</td>
                              <td className="py-3 px-4 text-sm text-slate-600">{commission.customer_name}</td>
                              <td className="py-3 px-4 text-sm text-slate-900 text-right">{formatCurrency(commission.gross_revenue)}</td>
                              <td className="py-3 px-4 text-sm font-medium text-green-600 text-right">{formatCurrency(commission.reseller_earnings)}</td>
                              <td className="py-3 px-4 text-center">{getStatusBadge(commission.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>No commission records yet</p>
                      <p className="text-sm">Commissions will appear here as your customers pay for subscriptions</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="payouts">
              <Card>
                <CardHeader>
                  <CardTitle>Payout History</CardTitle>
                  <CardDescription>Your payout requests and their status</CardDescription>
                </CardHeader>
                <CardContent>
                  {payouts.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Requested</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Amount</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Method</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Processed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.map((payout) => (
                            <tr key={payout.payout_id} className="border-b last:border-0 hover:bg-slate-50">
                              <td className="py-3 px-4 text-sm text-slate-900">{formatDate(payout.requested_at)}</td>
                              <td className="py-3 px-4 text-sm font-medium text-slate-900 text-right">{formatCurrency(payout.amount)}</td>
                              <td className="py-3 px-4 text-sm text-slate-600 capitalize">{payout.payout_method?.replace('_', ' ')}</td>
                              <td className="py-3 px-4 text-center">{getStatusBadge(payout.status)}</td>
                              <td className="py-3 px-4 text-sm text-slate-600">{formatDate(payout.processed_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <DollarSign size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>No payout requests yet</p>
                      <p className="text-sm">Request a payout when you have pending earnings</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Tag size={18} /> Plan Pricing</CardTitle>
                  <CardDescription>Set the monthly prices you charge your customers for each plan. These override the platform defaults for your reseller portal.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      { key: 'starter', label: 'Starter', color: 'slate', desc: 'Basic websites & content' },
                      { key: 'pro', label: 'Pro', color: 'indigo', desc: 'Newsletters + advanced features' },
                      { key: 'enterprise', label: 'Enterprise', color: 'violet', desc: 'Full platform access' },
                    ].map(({ key, label, color, desc }) => (
                      <div key={key} className={`p-5 border-2 rounded-xl border-${color}-200 bg-${color}-50/40`}>
                        <p className={`text-sm font-semibold text-${color}-700 mb-1`}>{label}</p>
                        <p className="text-xs text-slate-500 mb-4">{desc}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 font-medium">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={pricing[key]}
                            onChange={(e) => setPricing(p => ({ ...p, [key]: e.target.value }))}
                            className="font-semibold text-lg"
                            data-testid={`pricing-${key}`}
                          />
                          <span className="text-slate-400 text-sm">/mo</span>
                        </div>
                        {parseFloat(pricing[key]) !== platformDefaults[key] && (
                          <p className="text-xs text-indigo-600 mt-2">Platform default: ${platformDefaults[key]}/mo</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <p className="text-sm text-slate-500">Changes apply to new customer checkouts. Existing subscriptions are unaffected.</p>
                    <Button onClick={handleSavePricing} disabled={savingPricing} data-testid="save-pricing-btn">
                      <Save size={15} className="mr-2" />
                      {savingPricing ? 'Saving...' : 'Save Pricing'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Request Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Payout</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">Available for payout</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.pending_earnings)}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Amount (leave empty for full amount)</Label>
              <Input
                type="number"
                placeholder={`Max: ${formatCurrency(stats.pending_earnings)}`}
                value={payoutRequest.amount}
                onChange={(e) => setPayoutRequest({ ...payoutRequest, amount: e.target.value })}
              />
              <p className="text-xs text-slate-500">Minimum payout: $50.00</p>
            </div>

            <div className="space-y-2">
              <Label>Payout Method</Label>
              <Select
                value={payoutRequest.payout_method}
                onValueChange={(v) => setPayoutRequest({ ...payoutRequest, payout_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="stripe_connect">Stripe Connect</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Any special instructions..."
                value={payoutRequest.notes}
                onChange={(e) => setPayoutRequest({ ...payoutRequest, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutModal(false)}>Cancel</Button>
            <Button onClick={handleRequestPayout}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Billing Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Billing Email</Label>
              <Input
                type="email"
                value={billingSettings.billing_email}
                onChange={(e) => setBillingSettings({ ...billingSettings, billing_email: e.target.value })}
                placeholder="billing@company.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Preferred Payout Method</Label>
              <Select
                value={billingSettings.payout_method}
                onValueChange={(v) => setBillingSettings({ ...billingSettings, payout_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="stripe_connect">Stripe Connect</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <Label className="font-medium">Bank Account Details</Label>
              <Input
                placeholder="Bank Name"
                value={billingSettings.payout_details?.bank_name || ''}
                onChange={(e) => setBillingSettings({
                  ...billingSettings,
                  payout_details: { ...billingSettings.payout_details, bank_name: e.target.value }
                })}
              />
              <Input
                placeholder="Account Holder Name"
                value={billingSettings.payout_details?.account_holder || ''}
                onChange={(e) => setBillingSettings({
                  ...billingSettings,
                  payout_details: { ...billingSettings.payout_details, account_holder: e.target.value }
                })}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Account Number"
                  value={billingSettings.payout_details?.account_number || ''}
                  onChange={(e) => setBillingSettings({
                    ...billingSettings,
                    payout_details: { ...billingSettings.payout_details, account_number: e.target.value }
                  })}
                />
                <Input
                  placeholder="Routing Number"
                  value={billingSettings.payout_details?.routing_number || ''}
                  onChange={(e) => setBillingSettings({
                    ...billingSettings,
                    payout_details: { ...billingSettings.payout_details, routing_number: e.target.value }
                  })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tax ID / VAT Number</Label>
              <Input
                value={billingSettings.tax_id}
                onChange={(e) => setBillingSettings({ ...billingSettings, tax_id: e.target.value })}
                placeholder="XX-XXXXXXX"
              />
            </div>

            <div className="space-y-2">
              <Label>Business Address</Label>
              <Input
                value={billingSettings.business_address}
                onChange={(e) => setBillingSettings({ ...billingSettings, business_address: e.target.value })}
                placeholder="123 Business St, City, State ZIP"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateBillingSettings}>Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResellerBilling;
